import cloneDeep from 'lodash/cloneDeep'
import filter from 'lodash/filter'
import first from 'lodash/first'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { NativeModal, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { ErrorPromptModal } from '@Domains/Cart/Components/Prompt/Error/ErrorPrompt'
import { GiftCardTextPromptModal } from '@Domains/Cart/Components/Prompt/GiftCardText/GiftCardTextPrompt'
import { SizePromptV2 } from '@Domains/Cart/Components/Prompt/Size/SizePromptV2'
import { SuccessPromptV2 } from '@Domains/Cart/Components/Prompt/Successv2/SuccessPrompt'
import { TextPersonalizationPrompt } from '@Domains/Cart/Components/Prompt/TextPersonalization/Prompt'
import { AddToCartFuncSync } from '@Domains/Cart/Hooks/useAddToCart'
import { AddToCartMutation, AddToCartProductInput, Product, ProductCrossSellAndPersonalizationContentFragment } from '@Generated/graphql'

import styles from './Dialog.module.scss'

type Prompts = NonNullable<NonNullable<AddToCartMutation['addToCart']>['prompts']>
type Prompt = NonNullable<Prompts[0]>
export type PromptSelectVariant = Extract<Prompt, { __typename: 'AddToCartPromptSelectVariant' }>
interface PromptInput {
  prompts: Prompts
  promptsOrigin: AddToCartProductInput[] | null
}

interface PromptDialogProps extends PromptInput {
  isLoading?: boolean
  onClose: () => void
  onSubmit: AddToCartFuncSync
  portal?: boolean
}

const getSelectedPersonalizationType = (prompt: Prompts[0]): string | false => {
  if (prompt.__typename !== 'AddToCartPromptPersonalization') {
    return false
  }

  if (prompt.selectedPersonalization?.masterVariant?.type?.__typename !== 'BaseAttribute') {
    return 'unknown'
  }

  return (prompt.selectedPersonalization.masterVariant.type.value as any)?.key || 'unknown'
}

export const PromptDialog: FC<PromptDialogProps> = ({
  onClose,
  onSubmit,
  portal,
  prompts,
  promptsOrigin = [],
  isLoading = false,
}) => {
  const modalActionsRef = useNativeModalActions()
  const [sourceID, setSourceIDPrompt] = useState<string | null>(null)
  const [enableBackdropClick, setEnableBackdropClick] = useState(true)
  const variantModalInAction = useRef(false)
  const activePrompt = prompts?.[0] || null
  const [selectVariantPrompts, setSelectVariantPrompts] = useState<PromptSelectVariant[]>([])
  useEffect(() => {
    setSelectVariantPrompts((prev: PromptSelectVariant[]) => {
      const isSelectVariantPrompt = (p: Prompt): p is PromptSelectVariant => p.__typename === 'AddToCartPromptSelectVariant'
      const all = prompts.every(isSelectVariantPrompt)
      if (!all) {
        return []
      }
      const addToCartPromptSelectVariants = prompts.filter(isSelectVariantPrompt)
      return prev.length < addToCartPromptSelectVariants.length ? addToCartPromptSelectVariants : prev
    })
  }, [prompts])

  const closeModal = useCallback((callback?: () => void) => {
    setSelectVariantPrompts([])
    if (modalActionsRef.current) {
      modalActionsRef.current?.close()
    }
    if (callback) {
      callback()
    }
  }, [modalActionsRef])

  if (!activePrompt) {
    return null
  }
  if (variantModalInAction.current && activePrompt.__typename === 'AddToCartPromptSuccess') {
    variantModalInAction.current = false
    modalActionsRef.current?.runReloadAnimation()
  }

  if (activePrompt.__typename === 'AddToCartPromptGiftcard') {
    return (
      <GiftCardTextPromptModal
        actionRef={modalActionsRef}
        defaultOpen
        onClose={() => closeModal(onClose)}
        onSubmit={(message) => {
          const input: AddToCartProductInput[] = cloneDeep(promptsOrigin)!
          const amount = input[0]?.giftcard?.amount || 0
          input[0] = {
            ...input[0],
            giftcard: {
              amount,
              text: message,
            },
          }
          onSubmit(input)
          closeModal()
        }}
        portal={portal}
      />
    )
  }

  if (
    activePrompt.__typename === 'AddToCartPromptPersonalization'
    || activePrompt.__typename === 'AddToCartPromptSelectVariant'
    || activePrompt.__typename === 'AddToCartPromptSuccess'
  ) {
    const isPersonalization = activePrompt.__typename === 'AddToCartPromptPersonalization'
    const selectedPersonalizationType = isPersonalization && getSelectedPersonalizationType(activePrompt)
    const personalizations = isPersonalization && filter(activePrompt.personalizations || [], ['masterVariant.type.value.key', selectedPersonalizationType])
    // @ts-ignore
    const activePersonalizations: ProductCrossSellAndPersonalizationContentFragment | null = isPersonalization && activePrompt.personalizations

    variantModalInAction.current = ['AddToCartPromptSelectVariant', 'AddToCartPromptPersonalization'].includes(activePrompt.__typename)

    return (
      <NativeModal
        actionRef={modalActionsRef}
        alignment="right"
        backdrop
        className={styles.newDialog}
        closeOnBackDrop={enableBackdropClick}
        closeOnEscape={enableBackdropClick}
        defaultOpen
        onClose={onClose}
        portal={portal}
      >
        {activePrompt.__typename === 'AddToCartPromptSuccess' && (
          <SuccessPromptV2
            onClose={() => {
              closeModal(onClose)
              setSourceIDPrompt(null)
            }}
            onModalToggle={(v) => {
              if (v) {
                setEnableBackdropClick(!v)
              } else {
                setTimeout(() => setEnableBackdropClick(!v), 100)
              }
            }}
            origins={promptsOrigin}
            primaryLineItemID={activePrompt.primaryLineItemID || undefined}
            recommendations={activePrompt.recommendations || []}
            tactic={activePrompt.tactic}
          />
        )}
        {!isPersonalization && activePrompt.__typename === 'AddToCartPromptSelectVariant' && (
          <SizePromptV2
            loading={isLoading}
            onSubmit={(variant) => {
              const input: AddToCartProductInput[] = cloneDeep(promptsOrigin)!
              input[activePrompt.index].variantID = variant.variantID
              onSubmit(input, sourceID ? { sourceID } : undefined)
            }}
            product={activePrompt.product as Product}
            promptSelectVariants={selectVariantPrompts}
          />
        )}
        {isPersonalization && personalizations && (selectedPersonalizationType === 'deboss' || selectedPersonalizationType === 'engrave') && (
          <TextPersonalizationPrompt
            availablePersonalizations={personalizations}
            onSubmit={async (productID, personalization) => {
              const input: AddToCartProductInput[] = cloneDeep(promptsOrigin)!

              if (!personalization) {
                return onSubmit([first(input)!])
              }

              input[activePrompt.index].productID = productID!
              input[activePrompt.index].personalization = {
                ...input[activePrompt.index].personalization || {},
                text: personalization.text,
              }

              return onSubmit(input)
            }}
            selectedPersonalization={activePersonalizations || null}
            type={selectedPersonalizationType}
          />
        )}
      </NativeModal>
    )
  }

  if (activePrompt.__typename === 'AddToCartPromptError') {
    return (
      <ErrorPromptModal
        actionRef={modalActionsRef}
        code={activePrompt.code}
        defaultOpen
        message={activePrompt.message}
        onClose={() => closeModal(onClose)}
        portal={portal}
        title={'title' in activePrompt ? `${activePrompt.title}` : undefined}
      />
    )
  }

  return null
}
